/**
 * <s-sort-menu> — Styled sort dropdown that looks the same on phones, tablets and desktop.
 *
 * Wraps a visually hidden native <select name="sort_by"> (the value the form submits) and a
 * trigger button plus option list. Picking an option writes it to the select and dispatches a
 * bubbling `change`, so <s-live-form> re-renders results exactly as it does for a native select.
 *
 * Markup contract:
 *   [data-sort-trigger]  button that opens/closes the list (aria-expanded)
 *   [data-sort-panel]    the option list container (hidden when closed)
 *   [data-sort-option]   one button per option, with data-value matching a select option
 *   [data-sort-label]    span showing the current option label
 */
class SSortMenu extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const trigger = this.querySelector('[data-sort-trigger]');
    const panel = this.querySelector('[data-sort-panel]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', () => {
      if (this.hasAttribute('open')) {
        this.#close(false);
      } else {
        this.#open();
      }
    }, { signal });

    panel.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-sort-option]') : null;
      if (!target) return;
      this.#choose(target);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (!this.hasAttribute('open')) return;
      const options = [...this.querySelectorAll('[data-sort-option]')];
      const index = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        this.#close(true);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        options[(index + 1) % options.length]?.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        options[(index - 1 + options.length) % options.length]?.focus();
      }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (this.hasAttribute('open') && event.target instanceof Node && !this.contains(event.target)) {
        this.#close(false);
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #open() {
    const trigger = this.querySelector('[data-sort-trigger]');
    const panel = this.querySelector('[data-sort-panel]');
    this.setAttribute('open', '');
    trigger?.setAttribute('aria-expanded', 'true');
    if (panel) panel.hidden = false;
    const current = this.querySelector('[data-sort-option][aria-selected="true"]') || this.querySelector('[data-sort-option]');
    current?.focus();
  }

  #close(returnFocus) {
    const trigger = this.querySelector('[data-sort-trigger]');
    const panel = this.querySelector('[data-sort-panel]');
    this.removeAttribute('open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
    if (returnFocus) trigger?.focus();
  }

  #choose(option) {
    const select = this.querySelector('select');
    const label = this.querySelector('[data-sort-label]');
    const value = option.getAttribute('data-value') || '';
    this.querySelectorAll('[data-sort-option]').forEach((item) => {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    });
    if (label) label.textContent = option.textContent?.trim() || '';
    this.#close(true);
    if (select && select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('s-sort-menu')) {
  customElements.define('s-sort-menu', SSortMenu);
}
